import { useEffect, useState } from 'react';
import {
  diagramPermissionCheck,
  deleteDiagram,
  queryDiagrams,
  resetDiagramPagination,
} from '../store/dashboardActions';
import { useDashboardDiagramStore } from '../store/dashboardStore';
import CardHeader from './card/CardHeader';
import CardBody from './card/CardBody';
import CardList from './card/CardList';
import CardListItem from './card/CardListItem';
import Card from './card/Card';
import CardFooter from './card/CardFooter';
import { useAuthStore } from '../../auth/authStore';
import { PlusIcon } from '@heroicons/react/16/solid';
import { Button } from '../../../components/Form/Button';
import { Link } from 'react-router-dom';
import ItemMenu from '../../../components/ItemMenu/ItemMenu';
import ItemMenuListItem from '../../../components/ItemMenu/ItemMenuListItem';
import { openConfirmationModal } from '../../../shared/components/modal/openConfirmationModal';
import { openDiagramFormModal } from './DiagramFormModal';
import { DiagramModel } from '@diagram/model/diagram.model';
import SearchBar from '@shared/components/search/SearchBar';
import { addStatus } from '@shared/status/statusActions';

const Badge = ({ access }: { access: string }) => {
  const isPrivate = access === 'private';

  return (
    <span
      className={`inline-flex items-center rounded-md  px-1.5 py-0.5 text-xs font-medium  ring-1  ring-inset ${
        isPrivate
          ? 'bg-blue-50 text-blue-700 ring-blue-700/10'
          : 'text-green-700 bg-green-50 ring-green-600/20'
      }`}
    >
      {isPrivate ? 'Private' : 'Public'}
      <span className="sr-only">, {isPrivate ? 'private' : 'public'}</span>
    </span>
  );
};

const DiagramCard = () => {
  const diagrams = useDashboardDiagramStore((state) => state.items);
  const totalResults = useDashboardDiagramStore((state) => state.totalItems);
  const [filteredDiagrams, setFilteredDiagrams] = useState<Array<DiagramModel>>(
    [],
  );
  const diagramArray = Object.values(diagrams);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const user = useAuthStore((state) => state.auth);

  const userDiagrams = diagramArray.filter((diagram) => {
    if (!user) return false;
    const isPublic = diagram.access === 'public';
    const isUserDiagram = diagram.users[user.uid];
    return isPublic || isUserDiagram;
  });

  useEffect(() => {
    queryDiagrams();
    return () => {
      resetDiagramPagination();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (diagrams) {
      setFilteredDiagrams(userDiagrams);
    }
    // eslint-disable-next-line
  }, [diagrams]);

  if (!user) return null;

  //   REFACTOR: This will need to be updated to let the database handle filtering.

  const handlePrevious = () => {
    if (currentPage <= 1) return;
    setCurrentPage((prev) => prev - 1);
    queryDiagrams();
  };

  const handleNext = () => {
    if (totalResults <= pageSize * currentPage) return;
    setCurrentPage((prev) => prev + 1);
    queryDiagrams();
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchTerm = e.target.value;
    if (searchTerm) {
      const searchResult = filteredDiagrams.filter((diagram) =>
        diagram.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredDiagrams(searchResult);
      setCurrentPage(1);
    } else {
      setFilteredDiagrams(userDiagrams);
      setCurrentPage(1);
    }
  };

  const handleCreateDiagram = () => {
    openDiagramFormModal();
  };

  const editBtnOnClick = (diagram: DiagramModel) => {
    if (!diagram.id) {
      // TODO: Add logging if this case ever happens.
      addStatus({
        type: 'error',
        message: 'Diagram ID is missing. Cannot edit diagram.',
        autoDismiss: true,
        timeout: 5000,
      });
      return;
    }

    openDiagramFormModal(diagram);
  };

  return (
    <Card className="col-span-1 row-span-4">
      <CardHeader>
        <div className="flex flex-col w-full ml-1">
          <div className="flex items-center justify-between w-full">
            <div className="mt-2">
              <h3 className="text-base font-semibold text-gray-900">
                Diagrams
              </h3>
            </div>
            <div className="ml-4 mt-2 shrink-0">
              <Button
                type="button"
                variant="primary"
                onClick={handleCreateDiagram}
                className="flex items-center"
              >
                <PlusIcon className="size-5" aria-hidden="true" />
                <span className="ml-1">Create Diagram</span>
              </Button>
            </div>
          </div>
          {/* REFACTOR: I am not in love with this location, but can't think of something better at this point */}
          <SearchBar searchFunction={handleSearch} />
        </div>
      </CardHeader>
      <CardBody>
        <CardList>
          {filteredDiagrams.map((diagram) => {
            const canEdit = diagramPermissionCheck(
              'author',
              user.uid,
              diagram.users,
            );

            const handleDelete = () => {
              if (!canEdit) return;
              openConfirmationModal({
                message: 'Are you sure you want to delete this diagram?',
                onConfirm: () => deleteDiagram(diagram.id),
                variant: 'danger',
              });
            };

            return (
              <CardListItem key={diagram.id}>
                <div className="min-w-0">
                  <div className="flex items-center gap-x-3">
                    <Link
                      to={`/v1/diagram/${diagram.id}`}
                      className="text-sm/6 font-semibold text-primary hover:text-secondary"
                    >
                      {diagram.name}
                    </Link>
                    <Badge access={diagram.access} />
                  </div>
                  <p className="text-xs/6 text-gray-500 pt-0+++++++">
                    {diagram.description || ' No description provided'}
                  </p>
                </div>
                <div className="flex flex-none items-center gap-x-4">
                  <ItemMenu>
                    <ItemMenuListItem
                      label="View"
                      as={Link}
                      to={`/v1/diagram/${diagram.id}`}
                    />
                    <ItemMenuListItem
                      label="Edit"
                      as={Button}
                      onClick={() => editBtnOnClick(diagram)}
                      disabled={!canEdit}
                    />
                    <ItemMenuListItem
                      label="Delete"
                      as={Button}
                      onClick={handleDelete}
                      disabled={!canEdit}
                    />
                  </ItemMenu>
                </div>
              </CardListItem>
            );
          })}
        </CardList>
      </CardBody>
      {diagramArray.length >= pageSize && (
        <CardFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          currentPage={currentPage}
          pageSize={pageSize}
          totalResults={totalResults}
        />
      )}
    </Card>
  );
};
export default DiagramCard;
